import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { Offcanvas } from "bootstrap";
import { useNavigate } from "react-router-dom";
import * as caseService from "../Services/caseService";
// import { io } from "socket.io-client";

const Notification = forwardRef(({ onCountChange }, ref) => {
    const navigate = useNavigate();

    const offcanvasRef = useRef(null);
    const bsOffcanvas = useRef(null);

    const previousNotificationIds = useRef([]);
    const firstLoad = useRef(true);
    const pollingRef = useRef(null);
    const isNavigatingRef = useRef(false);

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [showCaseOptions, setShowCaseOptions] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    // const API_URL = process.env.REACT_APP_URL;
    

    // const socket = io(API_URL, {transports: [ "polling"],});

    useEffect(() => {
        const element = offcanvasRef.current;

        if (!element) {
            return;
        }

        try {
            bsOffcanvas.current =
                Offcanvas.getOrCreateInstance(element);
        } catch (error) {
            console.error(
                "Notification offcanvas initialization error:",
                error
            );
        }

        return () => {
            try {
                const instance =
                    Offcanvas.getInstance(element);

                if (instance) {
                    instance.hide();
                }

                bsOffcanvas.current = null;
            } catch (error) {
                console.error(
                    "Notification offcanvas cleanup error:",
                    error
                );
            }
        };
    }, []);


    const getOffcanvasInstance = () => {
        const element = offcanvasRef.current;

        if (!element) {
            return null;
        }

        try {
            return (
                Offcanvas.getInstance(element) ||
                Offcanvas.getOrCreateInstance(element)
            );
        } catch (error) {
            console.error(
                "Get offcanvas instance error:",
                error
            );

            return null;
        }
    };


    const hideOffcanvas = () => {
        try {
            const instance =
                getOffcanvasInstance();

            if (instance) {
                instance.hide();
            }
        } catch (error) {
            console.error(
                "Notification offcanvas hide error:",
                error
            );
        }
    };


    const navigateAfterOffcanvasClose = (path) => {
        if (!path) {
            return;
        }

        const element = offcanvasRef.current;

        if (!element) {
            navigate(path);
            return;
        }

        const instance =
            getOffcanvasInstance();

        if (!instance) {
            navigate(path);
            return;
        }

        if (!element.classList.contains("show")) {
            navigate(path);
            return;
        }

        if (isNavigatingRef.current) {
            return;
        }

        isNavigatingRef.current = true;

        const handleHidden = () => {
            try {
                navigate(path);
            } finally {
                isNavigatingRef.current = false;
            }
        };

        element.addEventListener(
            "hidden.bs.offcanvas",
            handleHidden,
            {
                once: true,
            }
        );

        instance.hide();
    };


    const playNotificationSound = () => {
        try {
            const AudioContext =
                window.AudioContext ||
                window.webkitAudioContext;

            if (!AudioContext) {
                return;
            }

            const audioContext = new AudioContext();

            const playTone = (
                frequency,
                startTime,
                duration,
                volume = 0.6
            ) => {
                const oscillator =
                    audioContext.createOscillator();

                const gain =
                    audioContext.createGain();

                oscillator.connect(gain);
                gain.connect(audioContext.destination);

                oscillator.type = "sine";

                oscillator.frequency.setValueAtTime(
                    frequency,
                    startTime
                );

                gain.gain.setValueAtTime(
                    0.001,
                    startTime
                );

                gain.gain.exponentialRampToValueAtTime(
                    volume,
                    startTime + 0.02
                );

                gain.gain.exponentialRampToValueAtTime(
                    0.001,
                    startTime + duration
                );

                oscillator.start(startTime);

                oscillator.stop(
                    startTime + duration + 0.03
                );
            };

            const now = audioContext.currentTime;

            // First tone
            playTone(
                850,
                now,
                0.25,
                0.65
            );

            // Second tone
            playTone(
                1100,
                now + 0.18,
                0.30,
                0.60
            );

            setTimeout(() => {
                try {
                    audioContext.close();
                } catch (error) {
                    // Ignore audio close error
                }
            }, 1000);

        } catch (error) {
            console.error(
                "Notification sound error:",
                error
            );
        }
    };

    useEffect(() => {
        getNotifications();
    }, []);


    const getNotifications = async () => {
        try {
            setLoading(true);

            const response = await caseService.getNotifications();

            if (response?.data?.status === "success") {
                const notificationData =
                    response?.data?.data || [];

                // Current notification IDs
                const currentNotificationIds =
                    notificationData.map(
                        (notification) => notification.id
                    );

                // Check for NEW notifications
                if (!firstLoad.current) {
                    const newNotifications =
                        notificationData.filter(
                            (notification) =>
                                !previousNotificationIds.current.includes(
                                    notification.id
                                )
                        );

                    if (newNotifications.length > 0) {
                        playNotificationSound();
                    }
                }

                // First API call par sound nahi bajana
                firstLoad.current = false;

                // Save current IDs for next polling
                previousNotificationIds.current =
                    currentNotificationIds;

                setNotifications(notificationData);

                const unreadCount =
                    notificationData.filter(
                        (notification) => !notification.isRead
                    ).length;

                onCountChange?.(unreadCount);
            }
        } catch (error) {
            console.log(
                "Notification Error:",
                error
            );
        } finally {
            setLoading(false);
        }
    };

    // useEffect(() => {
    //     const handleNewNotification = (newNotification) => {
    //         // console.log(" Socket New Notification:", newNotification);

    //         getNotifications();
    //     };

    //     socket.on("newNotification", handleNewNotification);

    //     return () => {
    //         socket.off("newNotification", handleNewNotification);
    //     };
    // }, []);


    const removeNotification = async (
        notificationId
    ) => {
        if (!notificationId) {
            return;
        }

        try {
            setRemovingId(notificationId);

            const response =
                await caseService.deleteNotification(
                    notificationId
                );

            if (
                response?.data?.status ===
                "success"
            ) {
                setNotifications((prev) =>
                    prev.filter(
                        (notification) =>
                            notification.id !==
                            notificationId
                    )
                );

                previousNotificationIds.current =
                    previousNotificationIds.current.filter(
                        (id) =>
                            id !== notificationId
                    );
            }
        } catch (error) {
            console.error(
                "Delete notification error:",
                error
            );
        } finally {
            setRemovingId(null);
        }
    };

    const handleNotificationClick = (
        notification
    ) => {
        if (!notification) {
            return;
        }

        if (!notification.caseId) {
            return;
        }

        const accountType =
            String(
                notification.accountType || ""
            )
                .trim()
                .toUpperCase();

        /*
         * B2B
         */

        if (accountType === "B2B") {
            navigate(
                `/b2b/cases/activity/${notification.caseId}`,
                {
                    state: {
                        caseId: notification.caseId,
                    },
                }
            );

            return;
        }

        /*
         * B2C
         */

        if (accountType === "B2C") {
            navigate(
                `/b2c/cases/activity/${notification.caseId}`,
                {
                    state: {
                        caseId: notification.caseId,
                    },
                }
            );
            return;
        }

        /*
         * BOTH
         */

        if (
            accountType === "BOTH" ||
            accountType === "3"
        ) {
            setSelectedNotification(
                notification
            );

            setShowCaseOptions(true);

            return;
        }
    };


    const handleBothRedirect = (type) => {
        if (!selectedNotification?.caseId) {
            return;
        }

        const caseId = selectedNotification.caseId;

        let path = "";

        if (type === "B2B") {
            path = `/b2b/cases/activity/${caseId}`;
        }

        if (type === "B2C") {
            path = `/b2c/cases/activity/${caseId}`;
        }

        if (!path) {
            return;
        }

        setShowCaseOptions(false);

        navigateAfterOffcanvasClose(path, {
            state: {
                caseId: caseId,
                fromNotification: true,
            },
        });

        setSelectedNotification(null);
    };


    useEffect(() => {
        getNotifications();

        pollingRef.current =
            setInterval(() => {
                getNotifications();
            }, 10000);

        return () => {
            if (pollingRef.current) {
                clearInterval(
                    pollingRef.current
                );

                pollingRef.current = null;
            }
        };
    }, []); 







    useEffect(() => {
        const handleEscape = (event) => {
            if (
                event.key === "Escape" &&
                showCaseOptions
            ) {
                setShowCaseOptions(false);
                setSelectedNotification(null);
            }
        };

        document.addEventListener(
            "keydown",
            handleEscape
        );

        return () => {
            document.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [showCaseOptions]);


    useImperativeHandle(ref, () => ({
        show() {
            try {
                const instance =
                    getOffcanvasInstance();

                if (!instance) {
                    return;
                }

                instance.show();

                getNotifications();
            } catch (error) {
                console.error(
                    "Notification show error:",
                    error
                );
            }
        },

        hide() {
            hideOffcanvas();
        },
    }));


    const formatDate = (date) => {
        if (!date) {
            return "";
        }

        try {
            return new Date(
                date
            ).toLocaleString(
                "en-IN",
                {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                }
            );
        } catch (error) {
            return "";
        }
    };


    const getNotificationIcon = (
        notification
    ) => {
        if (
            notification?.type ===
            "CASE_ASSIGNED"
        ) {
            return "fas fa-briefcase";
        }

        if (
            notification?.type ===
            "CHAT_MESSAGE"
        ) {
            return "fas fa-comment-dots";
        }

        return "fas fa-bell";
    };

    const getIconBackground = (
        notification
    ) => {
        if (
            notification?.type ===
            "CASE_ASSIGNED"
        ) {
            return "#e8f1ff";
        }

        if (
            notification?.type ===
            "CHAT_MESSAGE"
        ) {
            return "#f1eaff";
        }

        return "#fff5db";
    };

    const getIconColor = (
        notification
    ) => {
        if (
            notification?.type ===
            "CASE_ASSIGNED"
        ) {
            return "#2563eb";
        }

        if (
            notification?.type ===
            "CHAT_MESSAGE"
        ) {
            return "#7c3aed";
        }

        return "#d97706";
    };

    const unreadCount =
        notifications.filter(
            (notification) =>
                !notification.isRead
        ).length;

    return (
        <>
            {/* =========================================================
            NOTIFICATION OFFCANVAS
        ========================================================= */}

            <div
                ref={offcanvasRef}
                className="offcanvas offcanvas-end"
                tabIndex="-1"
                style={{
                    width: "430px",
                    maxWidth: "100%",
                    border: "none",
                    boxShadow: "-12px 0 40px rgba(15, 23, 42, 0.15)",
                    background: "#f8fafc",
                }}
            >
                {/* =====================================================
                HEADER
            ===================================================== */}

                <div
                    style={{
                        background:
                            "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                        padding: "20px 18px 18px",
                        color: "#fff",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* LEFT SIDE */}

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: 0,
                            }}
                        >
                            {/* =================================================
                            BELL ICON + COUNT BADGE
                        ================================================= */}

                            <div
                                style={{
                                    position: "relative",
                                    width: "44px",
                                    height: "44px",
                                    minWidth: "44px",
                                    borderRadius: "12px",
                                    background:
                                        "rgba(255,255,255,0.16)",
                                    border:
                                        "1px solid rgba(255,255,255,0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "12px",
                                }}
                            >
                                <i
                                    className="fas fa-bell"
                                    style={{
                                        fontSize: "19px",
                                        color: "#facc15",
                                    }}
                                ></i>

                                {/* COUNT BADGE */}

                                {unreadCount > 0 && (
                                    <span
                                        style={{
                                            position: "absolute",
                                            top: "-7px",
                                            right: "-7px",
                                            minWidth: "21px",
                                            height: "21px",
                                            padding: "0 5px",
                                            borderRadius: "50%",
                                            background: "#ef4444",
                                            color: "#fff",
                                            border:
                                                "2px solid #2563eb",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "9px",
                                            fontWeight: "800",
                                            lineHeight: 1,
                                            boxSizing: "border-box",
                                            boxShadow:
                                                "0 2px 6px rgba(0,0,0,0.18)",
                                        }}
                                    >
                                        {unreadCount > 99
                                            ? "99+"
                                            : unreadCount}
                                    </span>
                                )}
                            </div>

                            {/* =================================================
                            TITLE
                        ================================================= */}

                            <div
                                style={{
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "17px",
                                        fontWeight: "700",
                                        lineHeight: "1.3",
                                    }}
                                >
                                    Notifications
                                </div>

                                <div
                                    style={{
                                        marginTop: "3px",
                                        fontSize: "11px",
                                        color:
                                            "rgba(255,255,255,0.72)",
                                    }}
                                >
                                    Stay updated with your activity
                                </div>
                            </div>
                        </div>

                        {/* =================================================
                        CLOSE BUTTON
                    ================================================= */}

                        <button
                            type="button"
                            onClick={hideOffcanvas}
                            aria-label="Close notifications"
                            style={{
                                width: "32px",
                                height: "32px",
                                minWidth: "32px",
                                border:
                                    "1px solid rgba(255,255,255,0.25)",
                                borderRadius: "9px",
                                background:
                                    "rgba(255,255,255,0.12)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                marginLeft: "10px",
                            }}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* =========================================================
                NOTIFICATION TOOLBAR
            ========================================================= */}

                <div
                    style={{
                        background: "#fff",
                        borderBottom: "1px solid #e8edf3",
                        padding: "11px 17px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {/* UNREAD STATUS */}

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                        }}
                    >
                        <span
                            style={{
                                width: "7px",
                                height: "7px",
                                borderRadius: "50%",
                                background:
                                    unreadCount > 0
                                        ? "#2563eb"
                                        : "#94a3b8",
                            }}
                        />

                        <span
                            style={{
                                fontSize: "11px",
                                color: "#64748b",
                                fontWeight: "600",
                            }}
                        >
                            {unreadCount > 0
                                ? `${unreadCount} unread`
                                : "You're all caught up"}
                        </span>
                    </div>

                    {/* REFRESH */}

                    <button
                        type="button"
                        onClick={getNotifications}
                        disabled={loading}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#2563eb",
                            fontSize: "11px",
                            fontWeight: "700",
                            cursor: loading
                                ? "not-allowed"
                                : "pointer",
                            padding: "3px 5px",
                            opacity: loading ? 0.6 : 1,
                        }}
                    >
                        <i
                            className={`fas fa-sync-alt me-1 ${loading ? "fa-spin" : ""
                                }`}
                        ></i>

                        Refresh
                    </button>
                </div>

                {/* =========================================================
                BODY
            ========================================================= */}

                <div
                    className="offcanvas-body p-0"
                    style={{
                        overflowY: "auto",
                        background: "#f7f9fc",
                    }}
                >
                    {/* =====================================================
                    LOADING
                ===================================================== */}

                    {loading && notifications.length === 0 ? (
                        <div
                            style={{
                                minHeight: "400px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                className="spinner-border"
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    color: "#2563eb",
                                }}
                            />

                            <div
                                style={{
                                    marginTop: "13px",
                                    fontSize: "13px",
                                    color: "#64748b",
                                }}
                            >
                                Loading notifications...
                            </div>
                        </div>
                    ) : notifications.length === 0 ? (
                        /* =================================================
                           EMPTY STATE
                        ================================================= */

                        <div
                            style={{
                                minHeight: "430px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "30px",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    width: "78px",
                                    height: "78px",
                                    borderRadius: "50%",
                                    background: "#eaf2ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "18px",
                                }}
                            >
                                <i
                                    className="fas fa-bell-slash"
                                    style={{
                                        color: "#facc15",
                                        fontSize: "27px",
                                    }}
                                ></i>
                            </div>

                            <h6
                                style={{
                                    margin: "0 0 7px",
                                    color: "#1e293b",
                                    fontSize: "16px",
                                    fontWeight: "700",
                                }}
                            >
                                No Notifications
                            </h6>

                            <p
                                style={{
                                    margin: 0,
                                    maxWidth: "250px",
                                    color: "#94a3b8",
                                    fontSize: "12px",
                                    lineHeight: "1.6",
                                }}
                            >
                                You're all caught up. New
                                notifications will appear here.
                            </p>
                        </div>
                    ) : (
                        /* =================================================
                           NOTIFICATION LIST
                        ================================================= */

                        <div
                            style={{
                                padding: "10px 8px 20px",
                            }}
                        >
                            {notifications.map((notification) => {
                                const isUnread =
                                    !notification.isRead;

                                const isRemoving =
                                    removingId ===
                                    notification.id;

                                return (
                                    <div
                                        key={notification.id}
                                        onClick={() =>
                                            handleNotificationClick(
                                                notification
                                            )
                                        }
                                        style={{
                                            position: "relative",
                                            display: "flex",
                                            gap: "12px",
                                            padding: "15px 14px",
                                            marginBottom: "8px",
                                            borderRadius: "13px",
                                            background: isUnread
                                                ? "#f0f6ff"
                                                : "#fff",
                                            border: isUnread
                                                ? "1px solid #d9e8ff"
                                                : "1px solid #edf0f4",
                                            boxShadow: isUnread
                                                ? "0 3px 10px rgba(37,99,235,0.06)"
                                                : "0 2px 7px rgba(15,23,42,0.035)",
                                            cursor:
                                                notification.caseId
                                                    ? "pointer"
                                                    : "default",
                                            opacity: isRemoving
                                                ? 0.55
                                                : 1,
                                            transition:
                                                "all 0.2s ease",
                                        }}
                                    >
                                        {/* =================================================
                                        ICON
                                    ================================================= */}

                                        <div
                                            style={{
                                                width: "43px",
                                                height: "43px",
                                                minWidth: "43px",
                                                borderRadius: "11px",
                                                background:
                                                    getIconBackground(
                                                        notification
                                                    ),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <i
                                                className={getNotificationIcon(
                                                    notification
                                                )}
                                                style={{
                                                    color:
                                                        getIconColor(
                                                            notification
                                                        ),
                                                    fontSize: "16px",
                                                }}
                                            />
                                        </div>

                                        {/* =================================================
                                        CONTENT
                                    ================================================= */}

                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                paddingRight: "20px",
                                            }}
                                        >
                                            {/* TITLE */}

                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: "7px",
                                                    marginBottom:
                                                        "5px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#172033",
                                                        fontSize:
                                                            "13px",
                                                        fontWeight:
                                                            "700",
                                                        lineHeight:
                                                            "1.35",
                                                        overflow:
                                                            "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {
                                                        notification.title
                                                    }
                                                </div>

                                                {isUnread && (
                                                    <span
                                                        style={{
                                                            width: "7px",
                                                            height: "7px",
                                                            minWidth:
                                                                "7px",
                                                            borderRadius:
                                                                "50%",
                                                            background:
                                                                "#2563eb",
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            {/* MESSAGE */}

                                            <div
                                                style={{
                                                    color: "#64748b",
                                                    fontSize: "12px",
                                                    lineHeight:
                                                        "1.55",
                                                    marginBottom:
                                                        "8px",
                                                }}
                                            >
                                                {
                                                    notification.message
                                                }
                                            </div>

                                            {/* CASE */}

                                            {notification.caseId && (
                                                <div
                                                    style={{
                                                        display:
                                                            "inline-flex",
                                                        alignItems:
                                                            "center",
                                                        gap: "5px",
                                                        padding:
                                                            "4px 8px",
                                                        borderRadius:
                                                            "6px",
                                                        background:
                                                            "#edf5ff",
                                                        color: "#2563eb",
                                                        fontSize: "10px",
                                                        fontWeight:
                                                            "700",
                                                        marginBottom:
                                                            "7px",
                                                    }}
                                                >
                                                    <i className="fas fa-hashtag" />

                                                    {
                                                        notification.caseId
                                                    }

                                                    <i
                                                        className="fas fa-arrow-right"
                                                        style={{
                                                            fontSize:
                                                                "8px",
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* DATE */}

                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: "5px",
                                                    color: "#9aa5b5",
                                                    fontSize: "10px",
                                                }}
                                            >
                                                <i className="far fa-clock" />

                                                {formatDate(
                                                    notification.createdAt
                                                )}
                                            </div>
                                        </div>

                                        {/* =================================================
                                        DELETE
                                    ================================================= */}

                                        <button
                                            type="button"
                                            disabled={isRemoving}
                                            title="Remove notification"
                                            onClick={(event) => {
                                                event.stopPropagation();

                                                removeNotification(
                                                    notification.id
                                                );
                                            }}
                                            style={{
                                                position: "absolute",
                                                top: "9px",
                                                right: "8px",
                                                width: "26px",
                                                height: "26px",
                                                border: "none",
                                                borderRadius: "7px",
                                                background:
                                                    "transparent",
                                                color: "#a3adba",
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                cursor: isRemoving
                                                    ? "not-allowed"
                                                    : "pointer",
                                            }}
                                        >
                                            {isRemoving ? (
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    style={{
                                                        width: "13px",
                                                        height: "13px",
                                                    }}
                                                />
                                            ) : (
                                                <i
                                                    className="fas fa-times"
                                                    style={{
                                                        fontSize:
                                                            "11px",
                                                    }}
                                                />
                                            )}
                                        </button>

                                        {/* =================================================
                                        ARROW
                                    ================================================= */}

                                        {notification.caseId && (
                                            <div
                                                style={{
                                                    position:
                                                        "absolute",
                                                    right: "11px",
                                                    bottom: "12px",
                                                    color: "#cbd5e1",
                                                    fontSize: "9px",
                                                }}
                                            >
                                                <i className="fas fa-chevron-right" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* =========================================================
            BOTH CASE MODAL
        ========================================================= */}

            {showCaseOptions && (
                <div
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        setShowCaseOptions(false);
                        setSelectedNotification(null);
                    }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                        background: "rgba(15,23,42,0.52)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)",
                    }}
                >
                    <div
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                        style={{
                            width: "100%",
                            maxWidth: "390px",
                            borderRadius: "18px",
                            overflow: "hidden",
                            background: "#fff",
                            boxShadow:
                                "0 25px 70px rgba(15,23,42,0.25)",
                        }}
                    >
                        {/* =================================================
                        MODAL HEADER
                    ================================================= */}

                        <div
                            style={{
                                padding: "20px",
                                background:
                                    "linear-gradient(135deg, #2563eb, #1d4ed8)",
                                color: "#fff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "space-between",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "11px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            borderRadius: "11px",
                                            background:
                                                "rgba(255,255,255,0.15)",
                                            display: "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                        }}
                                    >
                                        <i className="fas fa-folder-open" />
                                    </div>

                                    <div>
                                        <div
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "700",
                                            }}
                                        >
                                            Open Case
                                        </div>

                                        <div
                                            style={{
                                                marginTop: "3px",
                                                fontSize: "11px",
                                                color:
                                                    "rgba(255,255,255,0.72)",
                                            }}
                                        >
                                            Case #
                                            {
                                                selectedNotification?.caseId
                                            }
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCaseOptions(
                                            false
                                        );
                                        setSelectedNotification(
                                            null
                                        );
                                    }}
                                    style={{
                                        width: "31px",
                                        height: "31px",
                                        border:
                                            "1px solid rgba(255,255,255,0.25)",
                                        borderRadius: "8px",
                                        background:
                                            "rgba(255,255,255,0.1)",
                                        color: "#fff",
                                        cursor: "pointer",
                                    }}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </div>
                        </div>

                        {/* =================================================
                        MODAL BODY
                    ================================================= */}

                        <div
                            style={{
                                padding: "22px",
                            }}
                        >
                            <div
                                style={{
                                    marginBottom: "18px",
                                    color: "#64748b",
                                    fontSize: "13px",
                                    lineHeight: "1.6",
                                }}
                            >
                                This case is available in both
                                B2B and B2C. Select where you want
                                to open the case.
                            </div>

                            {/* B2B */}

                            <button
                                type="button"
                                onClick={() =>
                                    handleBothRedirect("B2B")
                                }
                                style={{
                                    width: "100%",
                                    padding: "14px",
                                    marginBottom: "10px",
                                    border:
                                        "1px solid #dce9ff",
                                    borderRadius: "12px",
                                    background: "#f8fbff",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                }}
                            >
                                <div
                                    style={{
                                        width: "43px",
                                        height: "43px",
                                        minWidth: "43px",
                                        borderRadius: "11px",
                                        background: "#e7f0ff",
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                    }}
                                >
                                    <i
                                        className="fas fa-building"
                                        style={{
                                            color: "#2563eb",
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "13px",
                                            fontWeight: "700",
                                            color: "#1e293b",
                                        }}
                                    >
                                        Open in B2B
                                    </div>

                                    <div
                                        style={{
                                            marginTop: "3px",
                                            fontSize: "11px",
                                            color: "#94a3b8",
                                        }}
                                    >
                                        Open B2B case activity
                                    </div>
                                </div>

                                <i
                                    className="fas fa-chevron-right"
                                    style={{
                                        color: "#94a3b8",
                                        fontSize: "10px",
                                    }}
                                />
                            </button>

                            {/* B2C */}

                            <button
                                type="button"
                                onClick={() =>
                                    handleBothRedirect("B2C")
                                }
                                style={{
                                    width: "100%",
                                    padding: "14px",
                                    border:
                                        "1px solid #ece5ff",
                                    borderRadius: "12px",
                                    background: "#fcfaff",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                }}
                            >
                                <div
                                    style={{
                                        width: "43px",
                                        height: "43px",
                                        minWidth: "43px",
                                        borderRadius: "11px",
                                        background: "#f0eaff",
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                    }}
                                >
                                    <i
                                        className="fas fa-user"
                                        style={{
                                            color: "#7c3aed",
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "13px",
                                            fontWeight: "700",
                                            color: "#1e293b",
                                        }}
                                    >
                                        Open in B2C
                                    </div>

                                    <div
                                        style={{
                                            marginTop: "3px",
                                            fontSize: "11px",
                                            color: "#94a3b8",
                                        }}
                                    >
                                        Open B2C case activity
                                    </div>
                                </div>

                                <i
                                    className="fas fa-chevron-right"
                                    style={{
                                        color: "#94a3b8",
                                        fontSize: "10px",
                                    }}
                                />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

export default Notification;